/**
 * File Metadata Utilities
 *
 * Pure functions for handling file metadata in chat messages.
 * Extracted from ChatBloc to reduce class complexity.
 */

import { nanoid } from "nanoid";
import { FileUploadLimit } from "src/constants/limits";
import appErrors from "src/constants/appErrors";
import imageLoadPromise from "src/lib/imageLoadPromise";
import type { GetMessagesResponseItem } from "@9amhealth/openapi";
import asTemplateValue from "lib/asTemplateValue";
import { MessageMetaField, type MessageFileMetadata } from "../ChatBloc";

/**
 * Generate a metadata key for a file field
 */
export function getFileMetaKey(
  fileId: string,
  field: MessageMetaField
): string {
  return field.replace("<id>", fileId);
}

/**
 * Get a single metadata value for a file
 */
export function getMetaValue(
  metadata: Record<string, unknown>,
  fileId: string,
  field: MessageMetaField
): string {
  const key = getFileMetaKey(fileId, field);
  return metadata[key] ? String(metadata[key]) : "";
}

/**
 * Get all file metadata values for a given file ID
 */
export function getAllFileMetaValues(
  metadata: Record<string, unknown>,
  fileId: string
): MessageFileMetadata {
  const { name, size, type, source, imageWidth, imageHeight } =
    MessageMetaField;
  const values: MessageFileMetadata = {
    name: getMetaValue(metadata, fileId, name),
    size: getMetaValue(metadata, fileId, size),
    type: getMetaValue(metadata, fileId, type),
    source: getMetaValue(metadata, fileId, source),
    imageWidth: getMetaValue(metadata, fileId, imageWidth),
    imageHeight: getMetaValue(metadata, fileId, imageHeight)
  };
  return values;
}

/**
 * Extract file attachments from a message's metadata
 */
export function getMessageFileAttachments(
  message: GetMessagesResponseItem
): MessageFileMetadata[] {
  const meta = message.metadata ?? {};
  const files = asTemplateValue(meta.files);
  const fileString = files ? String(files) : "";
  return fileString
    .split(",")
    .filter(Boolean)
    .map((id) => getAllFileMetaValues(meta, id));
}

/**
 * Prepare file metadata for upload
 * @throws Error if file exceeds size limit
 */
export async function prepareFileMetaData(
  file: File
): Promise<Record<string, string>> {
  if (file.size > FileUploadLimit) {
    throw new Error(appErrors.upload_max_filesize_exceeded);
  }

  const metadata: {
    [key: string]: string;
    files: string;
  } = {
    files: ""
  };

  const fileId = nanoid(5);
  metadata.files = fileId;
  metadata[getFileMetaKey(fileId, MessageMetaField.name)] = file.name;
  metadata[getFileMetaKey(fileId, MessageMetaField.type)] = file.type;
  metadata[getFileMetaKey(fileId, MessageMetaField.size)] =
    file.size.toString();

  if (file.type.startsWith("image")) {
    const filePath = URL.createObjectURL(file);
    const img = await imageLoadPromise(filePath);
    metadata[getFileMetaKey(fileId, MessageMetaField.source)] = filePath;
    metadata[getFileMetaKey(fileId, MessageMetaField.imageHeight)] =
      img.height.toString();
    metadata[getFileMetaKey(fileId, MessageMetaField.imageWidth)] =
      img.width.toString();
  }

  return metadata;
}
